import type { Components, Theme } from "@mui/material/styles";
import { BW_MOTION } from "./motion";

const focusRing = (theme: Theme) => ({
  outline: `${theme.bw.focus.ringWidth}px solid ${theme.bw.focus.color}`,
  outlineOffset: theme.bw.focus.offset,
});

const selectedState = (theme: Theme) => ({
  "&.Mui-selected": {
    backgroundColor: theme.vars.palette.semantic.selected,
    color: theme.vars.palette.semantic.onSelected,
    "&:hover": {
      backgroundColor: theme.vars.palette.semantic.selected,
    },
  },
});

export const BW_COMPONENT_OVERRIDES: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      ":root": {
        colorScheme: "light dark",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.001ms !important",
          animationIterationCount: "1 !important",
          scrollBehavior: "auto !important",
          transitionDuration: "0.001ms !important",
        },
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: false,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: theme.bw.minimumTouchTarget,
        transitionDuration: `${BW_MOTION.duration.short}ms`,
        "&:focus-visible": focusRing(theme),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: theme.bw.minimumTouchTarget,
        "&.Mui-focused": focusRing(theme),
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Outlined inputs already expose MUI's strong focused fieldset. Do not
        // stack the shared InputBase ring around that native indicator: the
        // doubled rectangles read as an extra nested field, especially on
        // multiline composers. Keep one semantic, two-pixel focus boundary.
        "&.Mui-focused": {
          outline: "none",
          outlineOffset: 0,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.bw.focus.color,
            borderWidth: theme.bw.focus.ringWidth,
          },
        },
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: theme.bw.minimumTouchTarget,
        minHeight: theme.bw.minimumTouchTarget,
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => selectedState(theme),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => selectedState(theme),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:focus-visible": focusRing(theme),
      }),
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:focus-visible": focusRing(theme),
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: ({ theme }) => ({
        "&:focus-visible": focusRing(theme),
      }),
    },
  },
  MuiTooltip: {
    defaultProps: {
      // Keep the trigger's own accessible name; the tooltip augments its
      // description instead of silently replacing that name.
      describeChild: true,
    },
  },
};
